import { c as _c } from "react-compiler-runtime";
import { useEvent } from "@react-aria/utils";
import { useControlledState } from "@react-stately/utils";
import * as React from "react";
import { mergeProps } from "react-aria";
import { Button as AriaButton, ButtonContext, Select as AriaSelect, SelectContext, SelectValue as AriaSelectValue, useSlottedContext } from "react-aria-components";
import { ListBox } from "./ListBox.js";
import { Popover } from "./Popover.js";
import { selectValue } from "./SingleSelect.css.js";
import { FCTextFieldContext } from "./TextFieldContext.js";
import { ChevronButton } from "./shared/ChevronButton.js";
import { ClearButton } from "./shared/ClearButton.js";
import { InputBuilder } from "./shared/InputBuilder.js";
import { extractInputBuilderProps } from "./shared/inputBuilderHelper.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const DropdownValue = t0 => {
  const $ = _c(3);
  const {
    size,
    placeholder
  } = t0;
  let t1;
  if ($[0] !== placeholder || $[1] !== size) {
    t1 = /*#__PURE__*/_jsx(AriaSelectValue, {
      children: t2 => {
        const {
          selectedText,
          isPlaceholder
        } = t2;
        return /*#__PURE__*/_jsx("span", {
          className: selectValue({
            size,
            isPlaceholder
          }),
          children: isPlaceholder ? placeholder : selectedText
        });
      }
    });
    $[0] = placeholder;
    $[1] = size;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
function Select(t0) {
  const $ = _c(23);
  let buttonRef;
  let onOpenChange;
  let props;
  let triggerRef;
  if ($[0] !== t0) {
    ({
      triggerRef,
      buttonRef,
      onOpenChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = buttonRef;
    $[2] = onOpenChange;
    $[3] = props;
    $[4] = triggerRef;
  } else {
    buttonRef = $[1];
    onOpenChange = $[2];
    props = $[3];
    triggerRef = $[4];
  }
  const {
    inputBuilderProps,
    restProps
  } = extractInputBuilderProps(props);
  const selectProps = useSlottedContext(SelectContext);
  const buttonProps = useSlottedContext(ButtonContext);
  const T0 = AriaSelect;
  const t1 = mergeProps(selectProps, restProps);
  let t2;
  if ($[5] !== inputBuilderProps.size || $[6] !== props.placeholder) {
    t2 = /*#__PURE__*/_jsx(DropdownValue, {
      size: inputBuilderProps.size,
      placeholder: props.placeholder
    });
    $[5] = inputBuilderProps.size;
    $[6] = props.placeholder;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== buttonProps || $[9] !== buttonRef || $[10] !== t2) {
    t3 = /*#__PURE__*/_jsx(AriaButton, {
      ...buttonProps,
      ref: buttonRef,
      children: t2
    });
    $[8] = buttonProps;
    $[9] = buttonRef;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== props.children) {
    t4 = /*#__PURE__*/_jsx(ListBox, {
      children: props.children
    });
    $[12] = props.children;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t4 || $[15] !== triggerRef) {
    t5 = /*#__PURE__*/_jsx(Popover, {
      triggerRef: triggerRef,
      children: t4
    });
    $[14] = t4;
    $[15] = triggerRef;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== T0 || $[18] !== onOpenChange || $[19] !== t1 || $[20] !== t3 || $[21] !== t5) {
    t6 = /*#__PURE__*/_jsxs(T0, {
      onOpenChange: onOpenChange,
      ...t1,
      children: [t3, t5]
    });
    $[17] = T0;
    $[18] = onOpenChange;
    $[19] = t1;
    $[20] = t3;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
}
export function SingleSelect(t0) {
  const $ = _c(31);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = extractInputBuilderProps(props);
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    inputBuilderProps,
    restProps
  } = t1;
  const fcLabelProps = React.useContext(FCTextFieldContext);
  const buttonRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  let t2;
  if ($[4] !== fcLabelProps.labelRef) {
    t2 = fcLabelProps.labelRef ?? {
      current: null
    };
    $[4] = fcLabelProps.labelRef;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      e.stopPropagation();
      buttonRef.current?.focus();
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEvent(t2, "click", t3);
  const [isOpen, setIsOpen] = useControlledState(props.isOpen, false, props.onOpenChange);
  const [selectedKey, setSelectedKey] = useControlledState(props.selectedKey, props.defaultSelectedKey ?? null, props.onSelectionChange);
  let t4;
  if ($[7] !== isOpen || $[8] !== props.isRequired || $[9] !== selectedKey || $[10] !== setSelectedKey) {
    t4 = props.isRequired === true ? null : /*#__PURE__*/_jsx(ClearButton, {
      clearButton: true,
      isOpen: isOpen,
      hasSelection: selectedKey != null,
      onClear: () => {
        setSelectedKey(null);
      }
    });
    $[7] = isOpen;
    $[8] = props.isRequired;
    $[9] = selectedKey;
    $[10] = setSelectedKey;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== isOpen || $[13] !== setIsOpen) {
    t5 = () => {
      setIsOpen(!isOpen);
    };
    $[12] = isOpen;
    $[13] = setIsOpen;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== isOpen || $[16] !== t5) {
    t6 = /*#__PURE__*/_jsx(ChevronButton, {
      element: "div",
      isOpen: isOpen,
      onClick: t5
    });
    $[15] = isOpen;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = /*#__PURE__*/_jsxs(_Fragment, {
      children: [t4, t6]
    });
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== isOpen || $[22] !== restProps || $[23] !== selectedKey || $[24] !== setIsOpen || $[25] !== setSelectedKey) {
    t8 = /*#__PURE__*/_jsx(Select, {
      buttonRef: buttonRef,
      triggerRef: triggerRef,
      ...restProps,
      isOpen: isOpen,
      onOpenChange: setIsOpen,
      selectedKey: selectedKey,
      onSelectionChange: setSelectedKey
    });
    $[21] = isOpen;
    $[22] = restProps;
    $[23] = selectedKey;
    $[24] = setIsOpen;
    $[25] = setSelectedKey;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== inputBuilderProps || $[28] !== t7 || $[29] !== t8) {
    t9 = /*#__PURE__*/_jsx(InputBuilder, {
      ref: triggerRef,
      ...inputBuilderProps,
      right: t7,
      children: t8
    });
    $[27] = inputBuilderProps;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
//# sourceMappingURL=SingleSelect.js.map